import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { complianceGapChatApi } from '../../services/axios/AuthApis/TrustCenterApis';

/**
 * ComplianceGapChatbot – floating AI chat widget
 *
 * Props:
 *   frameworkId   {string|null}  – currently selected framework ObjectId
 *   frameworkName {string|null}  – display name shown in the chat header
 */
const ComplianceGapChatbot = ({ frameworkId = null, frameworkName = null }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: frameworkName
        ? `Hi! I'm your AI compliance analyst. I'm loaded with all controls for **${frameworkName}**. Ask me anything — gap analysis, control explanations, implementation tips, or "What am I missing?"`
        : `Hi! I'm your AI compliance analyst. Select a framework in the dashboard, then ask me about gaps, controls, or implementation requirements.`,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Reset greeting when framework changes
  useEffect(() => {
    setMessages([
      {
        role: 'assistant',
        content: frameworkName
          ? `Hi! I'm your AI compliance analyst. I'm loaded with all controls for **${frameworkName}**. Ask me anything — gap analysis, control explanations, implementation tips, or "What am I missing?"`
          : `Hi! I'm your AI compliance analyst. Select a framework in the dashboard, then ask me about gaps, controls, or implementation requirements.`,
      },
    ]);
  }, [frameworkId, frameworkName]);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const nextHistory = [...messages, userMsg];
    setMessages(nextHistory);
    setInput('');
    setLoading(true);

    try {
      // Send only role + content (no extra fields) as history
      const historyPayload = nextHistory.map(({ role, content }) => ({ role, content }));

      const data = await complianceGapChatApi(text, frameworkId, historyPayload.slice(0, -1));
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '⚠️ Something went wrong. Please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: frameworkName
          ? `Chat cleared. I'm still loaded with **${frameworkName}** controls. What would you like to explore?`
          : 'Chat cleared. Select a framework and ask me anything.',
      },
    ]);
  };

  const SUGGESTED = frameworkName
    ? [
        `What controls am I likely missing for ${frameworkName}?`,
        'Which controls are hardest to implement?',
        'Give me a gap analysis checklist.',
        'Explain the top 5 most critical controls.',
      ]
    : ['How do I get started with ISO 27001?', 'What is SOC 2 Type II?'];

  return ReactDOM.createPortal(
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[9999] flex items-center justify-center w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/40 transition-all duration-200 hover:scale-105 active:scale-95"
        title="AI Gap Analysis Chatbot"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <XMarkIcon className="w-6 h-6" />
            </motion.span>
          ) : (
            <motion.span
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <ChatBubbleLeftRightIcon className="w-6 h-6" />
            </motion.span>
          )}
        </AnimatePresence>
        {/* Pulse ring */}
        {!open && (
          <span className="absolute w-full h-full rounded-full bg-blue-500 opacity-30 animate-ping" />
        )}
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chatbox"
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            className="fixed bottom-24 right-6 z-[9999] w-[380px] max-w-[calc(100vw-1.5rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
            style={{ maxHeight: 'calc(100vh - 7rem)' }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-brand to-brand-dark text-white flex-shrink-0">
              <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm leading-tight">AI Gap Analyst</p>
                {frameworkName ? (
                  <p className="text-xs text-blue-100 truncate flex items-center gap-1">
                    <ShieldCheckIcon className="w-3 h-3 flex-shrink-0" />
                    {frameworkName}
                  </p>
                ) : (
                  <p className="text-xs text-blue-200">No framework selected</p>
                )}
              </div>
              <button
                onClick={clearChat}
                title="Clear chat"
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                      <SparklesIcon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-tr-sm'
                        : 'bg-white text-gray-800 border border-gray-200 rounded-tl-sm shadow-sm'
                    }`}
                  >
                    {renderMarkdown(msg.content)}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                    <SparklesIcon className="w-3.5 h-3.5 text-blue-600 animate-pulse" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3 py-2 shadow-sm">
                    <span className="flex gap-1 items-center">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
                    </span>
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* Suggestions (only show when 1 message = welcome) */}
            {messages.length === 1 && (
              <div className="px-3 py-2 border-t border-gray-100 bg-white flex-shrink-0">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5 px-1">
                  Suggested questions
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTED.map((q) => (
                    <button
                      key={q}
                      onClick={() => { setInput(q); inputRef.current?.focus(); }}
                      className="text-xs bg-blue-600 hover:bg-blue-700 text-white border border-blue-500 rounded-full px-2.5 py-1 transition-colors text-left"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="flex items-end gap-2 px-3 py-2.5 border-t border-gray-200 bg-white flex-shrink-0">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about gaps, controls, requirements…"
                rows={1}
                className="flex-1 resize-none border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent max-h-28 overflow-y-auto placeholder-gray-400"
                style={{ lineHeight: '1.4' }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
};

/**
 * Minimal markdown renderer: bold, inline code, bullet lists.
 * Avoids adding a markdown library dependency.
 */
function renderMarkdown(text) {
  if (!text) return null;

  const lines = text.split('\n');
  return lines.map((line, i) => {
    // Bullet points
    if (/^[-*]\s/.test(line)) {
      return (
        <li key={i} className="ml-3 list-disc">
          {inlineFormat(line.replace(/^[-*]\s/, ''))}
        </li>
      );
    }
    // Headings ##
    if (/^##\s/.test(line)) {
      return (
        <p key={i} className="font-semibold text-gray-900 mt-1">
          {inlineFormat(line.replace(/^##\s/, ''))}
        </p>
      );
    }
    // Empty line → spacer
    if (line.trim() === '') return <br key={i} />;

    return <p key={i}>{inlineFormat(line)}</p>;
  });
}

function inlineFormat(text) {
  // Split by **bold** and `code`
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (/^`[^`]+`$/.test(part)) {
      return (
        <code key={i} className="bg-gray-100 text-blue-700 px-1 rounded text-xs font-mono">
          {part.slice(1, -1)}
        </code>
      );
    }
    return part;
  });
}

export default ComplianceGapChatbot;
